import React, { useEffect, useRef, useState } from "react";
import styled, { useTheme } from "styled-components/native";
import { ScrollView, View } from "react-native";
import { Container, Input, Spacer, VDBText } from "@components/atoms";
import { useTypographyStore } from "@redux/slices/typographySlice";
import {
  useRequestHistoryDetailMessages,
  useSendRequestHistoryMessage,
} from "@apis/requestHistory";
import {
  MessageSentType,
  StockItemRequestMessage,
  StockItemRequestMessages,
  StockItemRequestMessagesSorted,
} from "@src/types/HistoryRequest";
import { SpaceType } from "@components/atoms/Spacer/types";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { requestChatSchema } from "./requestChatSchema";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import Loader from "@components/atoms/Loader";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { DEVICE_SIZE } from "@utils/index";
import { VDBIcons } from "@utils/VDBIcons";
import { KeyboardAwareScrollView } from "react-native-keyboard-controller";

const RequestChat = (props: any) => {
  const typography = useTypographyStore();
  const theme = useTheme();
  const { t } = useTranslation();
  const { stockItemRequest } = props;
  const userInfo = useUserInfo();
  const [validSend, setValidSend] = useState(false);
  const requestMessagesCall = useRequestHistoryDetailMessages(
    stockItemRequest.id
  );
  const [stockItemRequestMessages, setStockItemRequestMessages] = useState<
    StockItemRequestMessage[]
  >([]);
  const [requestMessages, setRequestMessages] = useState<
    StockItemRequestMessagesSorted[]
  >([]);
  const sendMessage = useSendRequestHistoryMessage(stockItemRequest.id);
  const scrollViewRef = useRef<ScrollView>(null);

  const {
    control,
    getValues,
    reset,
    formState: { isValid },
  } = useForm({
    resolver: yupResolver(requestChatSchema),
  });

  const getTime = (time: any) => {
    return time.format("hh:mm A");
  };

  const getDate = (dates: any) => {
    return dates.format("D MMM, YYYY");
  };

  const formatDate = (dateString: string, isDate: boolean) => {
    const date = moment(dateString);
    const today = moment();
    const yesterday = moment().subtract(1, "days");

    if (date.isSame(today, "day")) {
      return isDate ? "Today" : getTime(date);
    } else if (date.isSame(yesterday, "day")) {
      return isDate ? "Yesterday" : getTime(date);
    } else {
      return isDate ? getDate(date) : getTime(date);
    }
  };

  useEffect(() => {
    if (requestMessagesCall.isSuccess && requestMessagesCall.data.data.data) {
      let stock_item_request_messages = (
        requestMessagesCall.data?.data?.data as StockItemRequestMessages
      ).stock_item_request_messages as StockItemRequestMessage[];
      setStockItemRequestMessages(stock_item_request_messages);
    }
  }, [requestMessagesCall.data]);

  useEffect(() => {
    stockItemRequestMessages?.sort(function (a: any, b: any) {
      return (
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      );
    });

    const groupedMessages = stockItemRequestMessages?.length
      ? stockItemRequestMessages.reduce((acc, msg: StockItemRequestMessage) => {
          const dateKey = formatDate(msg.created_at, true);
          const time = formatDate(msg.created_at, false);
          const formattedMsg = { ...msg, time };

          if (!acc[dateKey]) {
            acc[dateKey] = [];
          }

          acc[dateKey].push(formattedMsg);
          return acc;
        }, {})
      : {};

    const sortedGroupedMessages = Object.keys(groupedMessages)?.length
      ? Object.keys(groupedMessages).map((dateKey) => {
          return {
            date: dateKey,
            messages: groupedMessages[dateKey],
          };
        })
      : [];

    setRequestMessages(sortedGroupedMessages);
    if (scrollViewRef.current) {
      scrollViewRef.current.scrollToEnd({ animated: true });
    }
  }, [stockItemRequestMessages]);

  const sendNewMessage = (message: string) => {
    if (isValid) {
      sendMessage.mutate(
        {
          message,
        },
        {
          onSuccess(successData: any) {
            const messageObject =
              successData.data?.data?.stock_item_request_message;
            const newObject = {
              id: messageObject?.id,
              stock_item_request_id: messageObject.stock_item_request_id,
              user_id: messageObject?.sender_id,
              name: "",
              message: messageObject?.message,
              created_at: messageObject?.created_at,
            };
            const requestMessagesList = [
              ...stockItemRequestMessages,
              newObject,
            ];
            setStockItemRequestMessages(requestMessagesList);
            reset({ message: "" });
          },
        }
      );
    }
  };

  useEffect(() => {
    const messageValue = getValues("message");
    if (isValid) {
      setValidSend(true);
    } else if (messageValue) {
      const trimmedValue = messageValue.trim();
      setValidSend(trimmedValue !== "");
    } else {
      setValidSend(false);
    }
  }, [isValid, getValues]);

  return (
    <View style={{ height: "100%" }}>
      <TitleContainer>
        <VDBText
          type={typography["H3-M"]}
          color={theme["core-05"]}
          textAlign="center"
        >
          {t("requestHistoryDetail.requestChat.modal.screenText.text")}{" "}
          {stockItemRequest.view_type === "buyer_view"
            ? stockItemRequest.seller_location.company_name
            : stockItemRequest?.buyer_location?.company_name}
        </VDBText>
      </TitleContainer>

      {requestMessagesCall.isInitialLoading ||
        (requestMessagesCall.isFetching && <Loader />)}

      <KeyboardAwareScrollView
        ref={scrollViewRef}
        keyboardShouldPersistTaps="always"
        disableScrollOnKeyboardHide={true}
        contentContainerStyle={{ flexGrow: 1 }}
      >
        <MainContainer>
          <Container>
            {requestMessages?.map((messages, i) => {
              return (
                <React.Fragment key={i}>
                  <DateHeading>
                    <VDBText>{messages.date}</VDBText>
                  </DateHeading>
                  {messages.messages?.map((message, j) => {
                    return (
                      <SingleMessageContainer
                        key={j}
                        type={
                          userInfo.user.id === message.user_id
                            ? MessageSentType.SENT
                            : MessageSentType.RECEIVED
                        }
                        message={message.message}
                        time={message.time}
                      />
                    );
                  })}
                </React.Fragment>
              );
            })}
          </Container>
        </MainContainer>
        <InputContainer>
          <Container>
            <Controller
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <Input
                  inputLabel={t(
                    "requestHistoryDetail.requestChat.modal.input.label.text"
                  )}
                  placeholder={t(
                    "requestHistoryDetail.requestChat.modal.input.label.text"
                  )}
                  leftIcon={undefined}
                  onBlur={onBlur}
                  onChangeText={(val) => {
                    onChange(val);
                    const trimmedValue = val.trim();
                    setValidSend(trimmedValue !== "");
                  }}
                  value={value}
                  rightButtonIcon={VDBIcons.SEND}
                  rightButtonIconColor={"core-04"}
                  rightButtonDisabled={sendMessage.isLoading || !validSend}
                  rightButtonIconOnPress={() => {
                    sendNewMessage(getValues("message"));
                  }}
                  disabled={sendMessage.isLoading}
                  onSubmitEditing={() => {
                    if (validSend && !sendMessage.isLoading) {
                      sendNewMessage(getValues("message"));
                    }
                  }}
                />
              )}
              name="message"
            />
          </Container>
        </InputContainer>
      </KeyboardAwareScrollView>
    </View>
  );
};

const SingleMessageContainer = (props: any) => {
  const theme = useTheme();
  const { type, message, time } = props;
  const { width: deviceWidth } = DEVICE_SIZE;

  return (
    <>
      <Spacer size={SpaceType.Small} />
      <MessageContainer type={type}>
        {type === MessageSentType.SENT && (
          <TimeContainer type={type}>
            <VDBText color={theme["core-06"]}>{time}</VDBText>
            <Spacer size={SpaceType.Small} />
          </TimeContainer>
        )}
        <MessageTextContainer type={type} deviceWidth={deviceWidth}>
          <VDBText
            color={
              theme[type === MessageSentType.RECEIVED ? "core-05" : "core-02"]
            }
          >
            {message}
          </VDBText>
        </MessageTextContainer>
        <Spacer size={SpaceType.Small} />
        {type === MessageSentType.RECEIVED && (
          <TimeContainer type={type}>
            <Spacer size={SpaceType.Small} />
            <VDBText color={theme["core-06"]}>{time}</VDBText>
          </TimeContainer>
        )}
      </MessageContainer>
      <Spacer size={SpaceType.Double} />
    </>
  );
};

const MainContainer = styled.View`
  background-color: ${({ theme }) => theme["core-01"]};
  flex-grow: 1;
`;
const MessageContainer = styled.View<{ type: MessageSentType }>`
  flex-direction: row;
  justify-content: ${({ type }) =>
    type === MessageSentType.SENT ? "flex-end" : "flex-start"};
  min-height: 33px;
  align-items: flex-end;
  flex-wrap: wrap;
`;
const MessageTextContainer = styled.View<{
  type: MessageSentType;
  deviceWidth: number;
}>`
  padding: ${SpaceType.Basic}px;
  border-top-right-radius: 8px;
  border-top-left-radius: 8px;
  border-bottom-right-radius: ${({ type }) =>
    type === MessageSentType.SENT ? "0px" : "8px"};
  border-bottom-left-radius: ${({ type }) =>
    type === MessageSentType.RECEIVED ? "0px" : "8px"};
  background-color: ${({ theme, type }) =>
    type === MessageSentType.RECEIVED ? theme["core-03"] : theme["core-06"]};
  max-width: ${({ deviceWidth, theme }) =>
    theme.isLargeScreen ? 576 : deviceWidth - 100}px;
`;
const InputContainer = styled.View`
  margin-top: ${SpaceType.Big}px;
  padding-top: ${SpaceType.Big}px;
  padding-bottom: ${SpaceType.ExtraLarge}px;
  background-color: ${({ theme }) => theme["core-01"]};
  flex-shrink: 1;
`;
const TimeContainer = styled.View<{ type: MessageSentType }>`
  flex-direction: row;
  flex: auto;
  justify-content: ${({ type }) =>
    type === MessageSentType.RECEIVED ? "flex-start" : "flex-end"};
`;
const TitleContainer = styled.View`
  padding-top: ${SpaceType.Double}px;
  margin-bottom: ${SpaceType.Basic}px;
  align-items: center;
`;

const DateHeading = styled.View`
  display: flex;
  justify-content: center;
  margin-bottom: ${SpaceType.Basic}px;
  padding: ${SpaceType.Basic}px ${SpaceType.Double}px;
  align-items: center;
`;
export default RequestChat;
